import DOMPurify from 'dompurify';

const EVENT_DESCRIPTION_ALLOWED_TAGS = [
  'p',
  'b',
  'strong',
  'em',
  'u',
  'h1',
  'h2',
  'h3',
  'ul',
  'ol',
  'li',
  'a',
  'br',
] as const;

const EVENT_DESCRIPTION_ALLOWED_ATTR = ['href', 'target', 'rel'] as const;

type EventDescriptionLike = {
  description: string | null;
  description_html?: string | null;
};

function normalizeLineEndings(value: string): string {
  return value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function createDocumentFromHtml(html: string): Document {
  return new DOMParser().parseFromString(html, 'text/html');
}

function normalizeInlineText(value: string): string {
  return value
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

function extractInlineText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }

  if (!(node instanceof HTMLElement)) {
    return '';
  }

  if (node.tagName === 'BR') {
    return '\n';
  }

  if (node.tagName === 'A') {
    const label = normalizeInlineText(
      Array.from(node.childNodes).map(extractInlineText).join('')
    );
    const href = node.getAttribute('href')?.trim() ?? '';

    if (!href) {
      return label;
    }

    if (!label) {
      return href;
    }

    return label === href ? href : `${label} (${href})`;
  }

  return Array.from(node.childNodes).map(extractInlineText).join('');
}

function extractBlockText(node: Node): string {
  if (!(node instanceof HTMLElement)) {
    return normalizeInlineText(extractInlineText(node));
  }

  if (node.tagName === 'UL' || node.tagName === 'OL') {
    const items = Array.from(node.children)
      .filter((child): child is HTMLLIElement => child.tagName === 'LI')
      .map((item, index) => {
        const marker = node.tagName === 'OL' ? `${index + 1}. ` : '• ';
        const itemText = normalizeInlineText(extractInlineText(item));
        return itemText ? `${marker}${itemText}` : '';
      })
      .filter(Boolean);

    return items.join('\n');
  }

  return normalizeInlineText(extractInlineText(node));
}

function getPlainTextFromSanitizedHtml(html: string) {
  const document = createDocumentFromHtml(html);
  const blocks = Array.from(document.body.childNodes)
    .map(extractBlockText)
    .map((value) => value.trim())
    .filter(Boolean);

  if (blocks.length === 0) {
    return '';
  }

  return blocks.join('\n\n').trim();
}

export function sanitizeEventDescriptionHtml(html: string | null | undefined) {
  const trimmed = html?.trim() ?? '';

  if (!trimmed) {
    return '';
  }

  const sanitized = DOMPurify.sanitize(trimmed, {
    ALLOWED_TAGS: [...EVENT_DESCRIPTION_ALLOWED_TAGS],
    ALLOWED_ATTR: [...EVENT_DESCRIPTION_ALLOWED_ATTR],
    ALLOW_DATA_ATTR: false,
  }).trim();

  if (!sanitized) {
    return '';
  }

  const document = createDocumentFromHtml(sanitized);
  Array.from(document.querySelectorAll('a')).forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer nofollow');
  });

  const normalized = document.body.innerHTML.trim();

  return normalized && getPlainTextFromSanitizedHtml(normalized)
    ? normalized
    : '';
}

export function getEventDescriptionPlainTextFromHtml(
  html: string | null | undefined
) {
  const sanitized = sanitizeEventDescriptionHtml(html);

  if (!sanitized) {
    return '';
  }

  return getPlainTextFromSanitizedHtml(sanitized);
}

export function convertPlainTextToEventDescriptionHtml(
  text: string | null | undefined
) {
  const normalized = normalizeLineEndings(text ?? '').trim();

  if (!normalized) {
    return '';
  }

  return normalized
    .split(/\n{2,}/)
    .map(
      (paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`
    )
    .join('');
}

export function getEventDescriptionEditorValue(event: EventDescriptionLike) {
  if (event.description_html) {
    const sanitizedHtml = sanitizeEventDescriptionHtml(event.description_html);

    if (sanitizedHtml) {
      return sanitizedHtml;
    }
  }

  return convertPlainTextToEventDescriptionHtml(event.description);
}

export function getEventDescriptionRenderHtml(event: EventDescriptionLike) {
  if (event.description_html) {
    const sanitizedHtml = sanitizeEventDescriptionHtml(event.description_html);

    if (sanitizedHtml) {
      return sanitizedHtml;
    }
  }

  return convertPlainTextToEventDescriptionHtml(event.description);
}

export function buildEventDescriptionPayload(descriptionHtml: string) {
  const description_html = sanitizeEventDescriptionHtml(descriptionHtml);
  const description = getEventDescriptionPlainTextFromHtml(description_html);

  return {
    description: description || null,
    description_html: description_html || null,
  };
}

export function normalizeEventDescriptionLinkUrl(url: string) {
  const trimmed = url.trim();

  if (!trimmed) {
    return '';
  }

  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
}
